import { ApiError } from '@/@types/generated'
import { Breadcrumb } from '@/components/Atoms/Breadcrumbs'
import Loading from '@/components/Atoms/Loading'
import { Header } from '@/components/ui/header'
import { useError } from '@/hooks/useError'
import MotorCarrierOpenCasesDialog from '@/modules/carriers/components/dialogs/MotorCarrierOpenCasesDialog'
import format from '@/lib/format'
import { buildPath } from '@/routes'
import { Suspense, useCallback, useEffect } from 'react'
import {
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router'
import { CompanyProvider, useCompany } from '../../hooks/company-provider'
import { RenewalProvider } from '../../hooks/renewal-provider'
import { isRenewable } from '../../lib/is-renewable'
import { isReRegisterableCertificate } from '../../lib/is-re-registerable'
import { RenewalSteps } from './RenewalSteps'
import { RenewalRequiredInsuranceGuard } from './RenewalRequiredInsuranceGuard'
import { RenewalInFlightGuard } from './RenewalInFlightGuard'

export function RenewalLayout({
  isReRegistration,
}: {
  isReRegistration?: boolean
}) {
  const { businessEntityId } = useParams<{ businessEntityId: string }>()
  const navigate = useNavigate()
  const { showError } = useError()

  const handleError = useCallback(
    (error: unknown) => {
      if (error instanceof ApiError && error.status === 404) {
        showError('Company not found')
      }
      navigate(buildPath('/companies'))
    },
    [navigate, showError]
  )

  return (
    <CompanyProvider id={businessEntityId!} onError={handleError}>
      <RenewalLayoutContent isReRegistration={isReRegistration ?? false} />
    </CompanyProvider>
  )
}

function RenewalLayoutContent({
  isReRegistration,
}: {
  isReRegistration: boolean
}) {
  const { company } = useCompany()
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const motorCarrier = company.motorCarrier

  const canRenew = (() => {
    if (!motorCarrier?.certificate?.expirationDate) return false
    return (
      isRenewable(motorCarrier.certificate) ||
      isReRegisterableCertificate(motorCarrier.certificate)
    )
  })()

  useEffect(() => {
    if (!motorCarrier) return

    const successPath = buildPath(
      isReRegistration
        ? '/companies/:businessEntityId/certificate/re-register/success'
        : '/companies/:businessEntityId/certificate/renewal/success',
      { businessEntityId: company.id }
    )
    if (!canRenew && pathname !== successPath)
      navigate(
        buildPath('/companies/:businessEntityId', {
          businessEntityId: company.id,
        })
      )
  }, [canRenew, motorCarrier, navigate, pathname, isReRegistration, company.id])

  if (!motorCarrier) return <Loading />

  // No certificate, redirect to company details
  if (!motorCarrier.certificate)
    return (
      <Navigate
        to={buildPath('/companies/:businessEntityId', {
          businessEntityId: company.id,
        })}
      />
    )

  // Re-registerable (Revoked or Voluntarily Surrendered) certificate on renewal path → redirect to re-register
  if (
    isReRegisterableCertificate(motorCarrier.certificate) &&
    !isReRegistration
  )
    return (
      <Navigate
        to={buildPath('/companies/:businessEntityId/certificate/re-register', {
          businessEntityId: company.id,
        })}
      />
    )

  // Non-re-registerable certificate on re-register path → redirect to renewal
  if (
    !isReRegisterableCertificate(motorCarrier.certificate) &&
    isReRegistration
  )
    return (
      <Navigate
        to={buildPath('/companies/:businessEntityId/certificate/renewal', {
          businessEntityId: company.id,
        })}
      />
    )

  return (
    <>
      <Breadcrumb
        label={format.CarrierName(company)}
        path={buildPath('/companies/:businessEntityId', {
          businessEntityId: company.id,
        })}
      />
      <Breadcrumb
        label={
          isReRegistration ? 'Re-Register Certificate' : 'Renew Certificate'
        }
        path={buildPath(
          isReRegistration
            ? '/companies/:businessEntityId/certificate/re-register'
            : '/companies/:businessEntityId/certificate/renewal',
          { businessEntityId: company.id }
        )}
      />

      <div className="flex flex-col gap-6">
        <Header variant="h1">
          {isReRegistration ? 'Re-Register Certificate' : 'Renew Certificate'}
        </Header>

        <RenewalProvider
          businessEntityId={company.id}
          carrierId={motorCarrier.id}
          certificate={motorCarrier.certificate}
          isReRegistration={isReRegistration}
        >
          <RenewalSteps businessEntityId={company.id}>
            <Suspense fallback={<Loading variant="fullscreen" />}>
              <RenewalRequiredInsuranceGuard>
                <RenewalInFlightGuard>
                  <Outlet />
                </RenewalInFlightGuard>
              </RenewalRequiredInsuranceGuard>
            </Suspense>
          </RenewalSteps>
        </RenewalProvider>
      </div>

      {motorCarrier.certificate?.certificateNumber && (
        <MotorCarrierOpenCasesDialog
          certificateNumber={motorCarrier.certificate.certificateNumber}
          isReRegistration={isReRegistration}
        />
      )}
    </>
  )
}
